<template>
    <div class="normal-container font-mori-regular">
        <hr class="border-t-1 mb-20 w-full border-gray-300" />
        <div class="flex flex-col md:flex-row md:space-x-40">
            <div v-if="openingHours" class="flex w-full flex-col md:w-2/5">
                <h2 class="mb-0 ml-2.5 font-monument-extended !text-lg md:mb-16 lg:ml-0 lg:!text-2xl">
                    {{ openingHoursTitle }}
                </h2>
                <div class="flex flex-1 flex-col">
                    <table class="mt-6 flex-1 table-auto text-left text-[13px] lg:w-full lg:text-base">
                        <tbody>
                            <tr
                                v-for="(day, index) in openingHours"
                                :key="day.name + index"
                                :class="{ 'bg-green-500 font-mori font-bold': day.isToday }"
                                class="flex h-12 items-center justify-between px-8 py-8 hover:bg-green-300 lg:px-4">
                                <td class="capitalize">{{ day.name }}</td>
                                <td class="flex justify-end">
                                    <span v-if="day.openingTime !== 'Closed' || day.closingTime !== 'Closed'">
                                        {{ day.openingTime }} - {{ day.closingTime }}
                                    </span>
                                    <span v-else> Gesloten </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="mt-8 flex w-full flex-col md:mt-0 md:w-1/2">
                <h2 class="mb-1 font-monument-extended text-lg font-normal lg:mb-4 lg:!text-2xl">
                    {{ locationTitle }}
                </h2>
                <p
                    class="mb-3 font-monument-extended text-[10px] font-normal tracking-[1.25px] md:text-lg md:tracking-normal lg:mb-10">
                    {{ siteConfig[0].address }}, {{ siteConfig[0].zipcode }},
                    {{ siteConfig[0].city }}
                </p>
                <div class="relative flex-1">
                    <NuxtImg
                        src="/maps.png"
                        alt="Map"
                        class="h-full w-full object-cover grayscale filter"
                        sizes="100vw sm:50vw md:400px" />
                    <Button
                        class="absolute bottom-5 left-0 right-0"
                        classes="!mx-auto !w-4/5 lg:!w-3/5 !font-normal text-center"
                        :link="getUrl(button)"
                        :buttonType="button.type"
                        :text="button.text"></Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted } from 'vue';
    const siteConfig = await useSiteSettings();

    const props = defineProps({
        openingHoursTitle: String,
        locationTitle: String,
        button: Object,
    });

    const buttonData = await useGetSeoUrl(props.button.internalLink._ref);

    const getUrl = button => {
        if (button.buttonType === 'external') {
            return button.externalUrl;
        }
        if (button.buttonType === 'internal' && buttonData[0]?.seoUrls[0]?.seoPathInfo) {
            return buttonData[0]?.seoUrls[0]?.seoPathInfo?.toLowerCase();
        } else {
            return '#';
        }
    };
    // Mapping from Dutch day names to English day names
    const dayMapping = {
        maandag: 'monday',
        dinsdag: 'tuesday',
        woensdag: 'wednesday',
        donderdag: 'thursday',
        vrijdag: 'friday',
        zaterdag: 'saturday',
        zondag: 'sunday',
    };

    const openingHours = ref();

    const daysOfWeek = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];
    const today = (new Date().getDay() + 6) % 7; // Adjust the day number to start the week on Monday

    onMounted(async () => {
        siteConfig.value = await useSiteSettings();

        if (siteConfig.value && siteConfig.value[0] && siteConfig.value[0].openingHours) {
            openingHours.value = daysOfWeek.map((day, index) => {
                const englishDay = dayMapping[day as keyof typeof dayMapping];
                const openingTimeKey = `${englishDay}OpeningTime`;
                const closingTimeKey = `${englishDay}ClosingTime`;

                return {
                    name: day,
                    openingTime: siteConfig.value[0].openingHours[englishDay].openingTime,
                    closingTime: siteConfig.value[0].openingHours[englishDay].closingTime,
                    isToday: index === today, // Set isToday to true for the current day
                };
            });
        }
    });
</script>
